import { useCallback, useState } from "react";

import { isAPIError } from "@/core/services";
import { readErrorDetails } from "@/core/utils";
import { createPapelObra } from "./papeis-obra.gateway";
import type { CreatePapelObraParams } from "./papeis-obra.types";

export const PAPEL_OBRA_FORBIDDEN_MESSAGE = "Você não tem permissão para cadastrar papéis na obra.";
export const PAPEL_OBRA_ERROR_MESSAGE = "Não foi possível cadastrar o papel. Tente novamente.";
export const PAPEL_OBRA_DUPLICATE_MESSAGE = "Já existe um papel cadastrado com este nome.";

export type CreatePapelObraResult =
  | { ok: true }
  | {
      ok: false;
      /** 403 — a tela mostra Toast "sem permissão". */
      forbidden: boolean;
      /** Mensagem geral (falha inesperada) para Toast. */
      message: string | null;
      /** 409 — vai no campo nome. */
      nameError: string | null;
      /** 400 em `janela.saida` / `janela` — vai no bloco da janela. */
      janelaError: string | null;
    };

const NO_FAILURE = {
  ok: false as const,
  forbidden: false,
  message: null,
  nameError: null,
  janelaError: null,
};

/** Nunca rejeita: devolve o resultado para a tela decidir onde mostrar o erro. */
export function useCreatePapelObra() {
  const [isSubmitting, setIsSubmitting] = useState(false);

  const create = useCallback(
    async (params: CreatePapelObraParams): Promise<CreatePapelObraResult> => {
      setIsSubmitting(true);

      try {
        await createPapelObra(params);
        return { ok: true };
      } catch (error) {
        if (!isAPIError(error)) return { ...NO_FAILURE, message: PAPEL_OBRA_ERROR_MESSAGE };

        if (error.status === 403) return { ...NO_FAILURE, forbidden: true };

        if (error.status === 409) {
          return { ...NO_FAILURE, nameError: error.message || PAPEL_OBRA_DUPLICATE_MESSAGE };
        }

        if (error.status === 400) {
          const details = readErrorDetails(error.details);
          const janelaError =
            details["janela.saida"] ?? details["janela.entrada"] ?? details.janela ?? null;
          const nameError = details.nome ?? null;

          if (janelaError || nameError) return { ...NO_FAILURE, janelaError, nameError };
          return { ...NO_FAILURE, message: error.message || PAPEL_OBRA_ERROR_MESSAGE };
        }

        return { ...NO_FAILURE, message: PAPEL_OBRA_ERROR_MESSAGE };
      } finally {
        setIsSubmitting(false);
      }
    },
    [],
  );

  return { create, isSubmitting };
}
